'use client';

import { useMemo, useCallback, useState } from 'react';
import useSearchParams from '@/hooks/useSearchParams';
import { event_filters_used } from '@/utils/gtm';

const FilterItemWithCheckbox = ({
  data: filter,
  onFilter,
  parent,
  isFromSearch,
  t,
  setSelected,
  selected,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { getFilterObj, setFilterQueryHandler } = useSearchParams();

  const checkedFilters = useMemo(
    () =>
      isFromSearch
        ? getFilterObj?.filters?.split(',')
        : (getFilterObj?.[filter?.code]?.split(',') ?? []),
    [getFilterObj, filter?.code, isFromSearch]
  );

  const handleFilter = useCallback(
    (key) => {
      const isChecked = checkedFilters.includes(String(key));

      const updatedFilters = isChecked
        ? checkedFilters.filter((el) => el !== String(key))
        : [...checkedFilters, String(key)];

      setFilterQueryHandler({
        [filter.code]: updatedFilters.length
          ? updatedFilters.join(',')
          : undefined,
      });

      if (typeof setSelected === 'function') {
        setSelected(updatedFilters?.length);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkedFilters, filter.code, setFilterQueryHandler]
  );

  const filteredList = useMemo(() => {
    if (!searchQuery?.trim()) return filter?.list ?? [];

    const query = searchQuery.toLowerCase();

    return (filter?.list ?? []).filter(
      (child) =>
        child?.Label?.toLowerCase().includes(query) ||
        child?.label?.toLowerCase().includes(query) ||
        child?.Key?.toString().toLowerCase().includes(query)
    );
  }, [filter?.list, searchQuery]);

  return (
    <div className='border-[#E4E4F9] pb-4 last-of-type:border-0 max-sm:border-b max-sm:pb-3'>
      {/* ✅ Search input */}
      <div className='mb-3'>
        <input
          type='search'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`${t('search_for')} ${
            isFromSearch ? t(filter?.title) : filter?.title
          }...`}
          className='border-border-50 w-full rounded-xl border px-3 py-2 text-sm'
        />
      </div>

      {/* ✅ Filtered list */}
      {filteredList.length > 0 ? (
        <ul className='space-y-2'>
          {filteredList.map((child) => {
            const childKey = child?.Key ?? child?.code;
            const isChecked = isFromSearch
              ? checkedFilters?.includes(
                  `${parent?.attribute}:${child?.code}`
                ) || false
              : checkedFilters?.includes(String(child?.Key));

            const onChangeHandler = () => {
              event_filters_used({
                filter_name: filter?.title,
                filter_value: child?.Label || child?.label,
                filter_value_id: child?.Key ?? child?.code,
                filter_action: isChecked ? 'remove' : 'apply',
              });

              if (isFromSearch) {
                // call parent's algolia filter handler (it will toggle the filter in algolia)
                onFilter?.(child, parent);

                const delta = isChecked ? -1 : 1;
                const newCount = Math.max(0, (Number(selected) || 0) + delta);
                setSelected(newCount);
              } else {
                // category-based filter (URL)
                handleFilter(childKey);
              }
            };

            return (
              <label
                key={child?.Key || child?.code}
                className='border-border-50 flex items-center justify-between rounded-xl border px-3 py-2'
              >
                <h3 className='text-sm'>{child?.Label || child?.label}</h3>
                <input
                  type='checkbox'
                  checked={!!isChecked}
                  onChange={onChangeHandler}
                  className='size-4 accent-(--brand,#1e2b55)'
                />
              </label>
            );
          })}
        </ul>
      ) : (
        <div className='py-6 text-center text-sm text-gray-500'>
          {t('no_results_found')}
        </div>
      )}
    </div>
  );
};

export default FilterItemWithCheckbox;
